import "./MultiForm.scss";
import Footer from "../../../../components/Footer/Footer";
import Header from "../../../../components/Header/Header";
import ProjectHero from "../../../../templates/ProjectHero/ProjectHero";
import ContentBlock from "../../../../templates/ContentBlock/ContentBlock";
import ProjectPanelDisplay from "../../../../templates/ProjectPanelDisplay/ProjectPanelDisplay";
import { Helmet } from 'react-helmet';

function MultiForm(){
    return(
        <div className="page-layout">
            <Helmet>
                <title>MultiForm Quick Check Flow - Sony Chen</title>
            </Helmet>

            <Header />
            <main className="page-layout__main">
                <section className="page-layout__project-wrapper">
                <ProjectHero 
                    tag="Signup Optimization"
                    header="MultiForm – Quick Check Flow"
                    body="The existing signup flow for Quick Check, a Capital One Canada product that helps users check their credit card pre-approval, was unclear and fragmented. As part of a broader site redesign initiative, we saw an opportunity to improve the form experience and better guide users through the process. This project focused on optimizing the flow to increase conversion rates while also migrating the UI to the new Lighthouse design system."
                    company="Capital One"
                    role="Product Designer"
                    year="2023"
                    src="https://res.cloudinary.com/dhifyp04n/image/upload/v1744417451/Frame_2_ehixjn.png"
                    alt="A preview of the redesigned Quick Check signup flow"
                />

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={false}
                        header="Context"
                        body="With the redesigned product detail pages now live, the visual and functional gap between the new UI and the legacy Quick Check flow became more apparent. Quick Check is a key acquisition funnel for Capital One Canada, and the CTA from the refreshed PDPs directs users straight into this experience. Given its importance in driving customer conversions, teams across the organization aligned on the need to review and redesign the flow to ensure consistency, usability, and alignment with the new system design."
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={true}
                        isList={false}
                        header="Understanding the ecosystem"
                        body="Quick Check is a pre-qualification platform that helps prospective customers determine their eligibility for credit cards before applying. After entering their information, users are guided to a recommendations page with card options they are guaranteed to be approved for. Once a card is selected, users move into the Dynamic Application Page (DAPP), which serves as the final step in the journey. While QK and DAPP are distinct product suites managed by separate teams, they are deeply connected in the overall signup flow. This project initially focused on redesigning the Quick Check experience, with DAPP later updated in 2024 to align with the new QK interface and create a seamless end-to-end experience."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744432829/Frame_183492_r8v224.png"
                        alt="Diagram showing the Quick Check to DAPP user flow"
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={true}
                        isList={false}
                        header="Legacy flow: Quick Check to DAPP"
                        body="This visual illustrates the legacy Quick Check experience and its connection to the Dynamic Application Page (DAPP). Previously, Quick Check was a single-page form where users entered all their information before reaching the recommendations page. After selecting a card, they were routed to DAPP for the final step. At that point, conditional routing logic determined the next step based on the selected card suite. Some users were directed to fund a secured card, while others saw different success or error states depending on eligibility and product type."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744432845/Frame_183509_bkkojl.png"
                        alt="Legacy Quick Check to DAPP flow"
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={true}
                        isList={false}
                        header="Opportunity"
                        body="Quick Check is one of Capital One Canada's most critical acquisition funnels, yet the experience had not been updated in several years. This presented a strong opportunity to revisit the flow through a UX lens. By identifying key usability gaps and streamlining the form experience, there was clear potential to improve user confidence, reduce friction, and ultimately drive a meaningful lift in sign-up conversions."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744529036/Frame_183491_gb2alb.png"
                        alt="Opportunity analysis of the outdated Quick Check flow"
                    />

                    <ContentBlock 
                    isText={true}
                    isImage={false}
                    isList={true}
                    useXIcon={true}
                    header=""
                    body="Key opportunities identified during the evaluation:"
                    listItems={[
                        "Form length may cause fatigue or drop-off",
                        "No review step before submission",
                        "Single-page format limits clarity and flow",
                        "Errors in input can directly impact results without warning"
                    ]}
                    />

                    <ContentBlock 
                        isText={false}
                        isImage={false}
                        isList={false}
                        isVideo={true}
                        videoSrc="https://res.cloudinary.com/dhifyp04n/video/upload/v1744434407/PDP_1_ndnyvr.mp4"
                    />

                    <ProjectPanelDisplay
                        header="Form structure benchmarking"
                        body="Competitor analysis showed that many platforms use a progressive disclosure approach to improve scannability and reduce cognitive load, especially on mobile. This insight guided how we restructured the Quick Check flow."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744434723/Frame_183513_tpcies.png"
                        alt="Competitor examples showcasing progressive disclosure and sectioned form design"
                        isReverse={false}
                    />

                    <ProjectPanelDisplay
                        header="Validating the form length hypothesis"
                        body="Quantitative research confirmed our hypothesis: mobile users found the invoicing form lengthy and time-consuming. With over 80% of traffic on mobile, breaking the flow into smaller sections became a clear opportunity to reduce friction and drop-off."
                        video="https://res.cloudinary.com/dhifyp04n/video/upload/v1744435334/Comp_1_aqouqa.mp4"
                        isReverse={true}
                    />

                    <ContentBlock 
                    isText={true}
                    isImage={true}
                    isList={false}
                    header="Iterative design process"
                    body="Multiple design iterations were explored to ensure alignment with the brand refresh and evolving experience standards. Each major update was reviewed with key stakeholders, allowing for feedback loops that ensured cross-functional alignment and buy-in throughout the design process."
                    image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744688938/Frame_183494_oiyxal.png"
                    alt="Design iterations reviewed with stakeholders to align with the brand refresh"
                    />

                    <ContentBlock 
                    isText={true}
                    isImage={true}
                    isList={false}
                    header="Final design"
                    body="The final design focuses on clarity and guidance throughout the user journey. Content and layout were structured to clearly communicate progress, helping users understand how many steps remain. This approach not only reduces uncertainty but also builds confidence and encourages completion, especially for mobile-first users navigating multi-step forms."
                    image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744437240/Frame_183502_zt0ppg.png"
                    alt="Final design showing step-by-step structure and progress indicators in the Quick Check flow"
                    />

                    <ContentBlock 
                        isText={false}
                        isImage={true}
                        isList={false}
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744437091/Frame_183503_vndmw4.png"
                        alt="Preview of all form content across the Quick Check flow"
                    />

                    <ProjectPanelDisplay
                        header="Multi-page form approach"
                        body="Research supported a shift from the legacy single-page design to a multi-page format, which reduces cognitive load and helps users follow the flow more easily. The clean UI, combined with subtle but effective progress indicators, improves clarity and keeps users engaged throughout the experience."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744432841/Frame_183505_aa22ho.png"
                        alt="Multi-page form design with clean UI and subtle progress indicators"
                    />

                    <ProjectPanelDisplay
                        header="Adding a review step"
                        body="Research revealed that users felt more confident submitting Quick Check when given the opportunity to review their input beforehand. Introducing a dedicated review page helped reduce anxiety about making mistakes and improved overall trust in the experience."
                        video="https://res.cloudinary.com/dhifyp04n/video/upload/v1744438476/Review_o2h6v9.mp4"
                        isReverse={true}
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={true}
                        isList={false}
                        header="Mobile-first optimization"
                        body="With the majority of users accessing Quick Check from mobile devices, the redesign prioritized a mobile-first approach. The new layout significantly reduces page length and improves usability, making the form easier to navigate and complete on smaller screens."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744432838/Frame_183504_kk95e4.png"
                        alt="Mobile-optimized Quick Check layout"
                    />

                    <ProjectPanelDisplay
                        header="Why tablet still matters"
                        body="Although tablet users make up only about 10% of traffic, designing for this breakpoint improves clarity and speeds up development. Having defined tablet layouts reduces guesswork for engineers, making implementation faster and more consistent across devices."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744438150/Frame_183507_yzzzki.png"
                        alt="Tablet layout exploration to support developer handoff and responsive scaling"
                        isReverse={true}
                    />

                    <ProjectPanelDisplay
                        header="Measured impact"
                        body="Post-launch metrics showed a 9.2% lift in completed sign-ups—a significant improvement for a high-volume acquisition flow like Quick Check. This result validated our design decisions, particularly around content clarity, progressive disclosure, and mobile-first optimization."
                        image="https://res.cloudinary.com/dhifyp04n/image/upload/v1744432848/Frame_183512_owvrcp.png"
                        alt="Post-launch metrics visual showing a 9.2% lift in sign-ups"
                        isReverse={false}
                    />

                    <ContentBlock 
                        isText={true}
                        isImage={false}
                        isList={true}
                        isVideo={false}
                        header="Multi-Form impact and what’s next"
                        body={`The results from the Quick Check redesign were strong, but this was only phase one. The long-term vision is to fully integrate the Dynamic Application Page (DAPP) into the Quick Check experience for a more cohesive and connected flow. One point of debate that emerged during this process was the inclusion of a back button. While UX research supports offering users flexibility, some business partners were concerned it might introduce friction and reduce momentum. The research was paused, and the discussion remains ongoing.\n\nMultipage test results:`}
                        listItems={[
                            "Quick Check submit rate is 47.29% — 9.3% higher than the control",
                            "Conversion rate is 11.26% — 7.5% higher than the control",
                            "Email Consent Capture rate is 63.5% — 49% higher than the control",
                            "The multi-form test is projected to generate over $1.7M in incremental PV"
                    ]}
                    />
                
                </section>

                <Footer 
                    noBackgroundColor="true"
                />
            </main>
        </div>
    );
};

export default MultiForm;